'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import sortBy from 'lodash/sortBy';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { formatBudgetName } from '@/modules/finances/lib';
import { reservesCategoriesParser, reservesGranularityParser } from '@/modules/finances/lib/finances-reserves-chart-search-params';
import { buildWaterfallSeries, getAnalyticForWaterfall, processDataForWaterfall, sumValuesFromMapKeys } from '@/modules/finances/lib/reserves-waterfall-chart-utils';
import { type Budget, GRANULARITY_OPTIONS } from '@/modules/finances/types';
import type { Option } from '@/modules/shared/components/form/multiselect';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import { getFinancesReservesAnalytics } from '../../services/finances-reserves-chart';
const DEFAULT_GRANULARITY = GRANULARITY_OPTIONS.Monthly;
const granularityOptions = [{
  label: GRANULARITY_OPTIONS.Monthly,
  value: GRANULARITY_OPTIONS.Monthly
}, {
  label: GRANULARITY_OPTIONS.Quarterly,
  value: GRANULARITY_OPTIONS.Quarterly
}, {
  label: GRANULARITY_OPTIONS.Annually,
  value: GRANULARITY_OPTIONS.Annually
}] as const;
const apiGranularityByValue: Record<(typeof granularityOptions)[number]['value'], 'monthly' | 'quarterly' | 'annual'> = {
  [GRANULARITY_OPTIONS.Monthly]: 'monthly',
  [GRANULARITY_OPTIONS.Quarterly]: 'quarterly',
  [GRANULARITY_OPTIONS.Annually]: 'annual'
};
interface UseReservesWaterfallChartParams {
  codePath: string;
  budgets: Budget[];
  allBudgets: Budget[];
  year: string;
}
function useReservesWaterfallChart(t0) {
  const $ = _c(73);
  const {
    codePath,
    budgets,
    allBudgets,
    year
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      to: "sm"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isMobile = useMediaQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      from: "sm",
      to: "lg"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isTablet = useMediaQuery(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "lg",
      to: "xl"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isDesktop1024 = useMediaQuery(t3);
  const [selectedGranularity, setSelectedGranularity] = useQueryState("reservesGranularity", reservesGranularityParser);
  const [activeElements, setActiveElements] = useQueryState("reservesCategories", reservesCategoriesParser);
  let t4;
  if ($[3] !== codePath) {
    t4 = codePath.split("/");
    $[3] = codePath;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const levelOfDetail = t4.length + 1;
  const granularityApiValue = apiGranularityByValue[selectedGranularity];
  let t5;
  if ($[5] !== allBudgets || $[6] !== codePath) {
    let t6;
    if ($[8] !== codePath) {
      t6 = budget => budget.codePath === codePath;
      $[8] = codePath;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const levelBudget = allBudgets.find(t6);
    t5 = formatBudgetName(levelBudget?.name ?? "");
    $[5] = allBudgets;
    $[6] = codePath;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const titleLevelBudget = t5;
  const titleChart = titleLevelBudget === "" || titleLevelBudget === "No-Name" ? "Reserves" : titleLevelBudget;
  let t6;
  if ($[10] !== codePath || $[11] !== levelOfDetail || $[12] !== selectedGranularity || $[13] !== year) {
    t6 = ["reserves-waterfall-analytics", selectedGranularity, year, codePath, levelOfDetail];
    $[10] = codePath;
    $[11] = levelOfDetail;
    $[12] = selectedGranularity;
    $[13] = year;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== budgets || $[16] !== levelOfDetail || $[17] !== selectedGranularity || $[18] !== year) {
    t7 = async () => getFinancesReservesAnalytics({
      granularity: selectedGranularity,
      year,
      lod: levelOfDetail,
      budgets
    });
    $[15] = budgets;
    $[16] = levelOfDetail;
    $[17] = selectedGranularity;
    $[18] = year;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = {
      queryKey: t6,
      queryFn: t7
    };
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const {
    data: analytics,
    isLoading
  } = useQuery(t8);
  let t9;
  if ($[23] !== allBudgets || $[24] !== analytics || $[25] !== budgets || $[26] !== granularityApiValue) {
    t9 = getAnalyticForWaterfall(budgets, granularityApiValue, analytics, allBudgets);
    $[23] = allBudgets;
    $[24] = analytics;
    $[25] = budgets;
    $[26] = granularityApiValue;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const {
    summaryValues,
    totalToStartEachBudget
  } = t9;
  let t10;
  if ($[28] !== summaryValues) {
    t10 = Array.from(summaryValues.keys());
    $[28] = summaryValues;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const selectAll = t10;
  let t11;
  if ($[30] !== activeElements) {
    t11 = activeElements ?? [];
    $[30] = activeElements;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== selectAll || $[33] !== t11) {
    let t13;
    if ($[35] !== selectAll) {
      t13 = element => selectAll.includes(element);
      $[35] = selectAll;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    t12 = t11.filter(t13);
    $[32] = selectAll;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const activeElementsFiltered = t12;
  const selectedElements = activeElements === null ? selectAll : activeElementsFiltered;
  let t13;
  if ($[37] !== granularityApiValue || $[38] !== isDesktop1024 || $[39] !== isMobile || $[40] !== isTablet || $[41] !== selectedElements || $[42] !== summaryValues || $[43] !== totalToStartEachBudget) {
    const valuesToShow = sumValuesFromMapKeys(summaryValues, selectedElements, granularityApiValue);
    const dataReady = processDataForWaterfall(valuesToShow, selectedElements, totalToStartEachBudget);
    t13 = buildWaterfallSeries(dataReady, isMobile, isTablet, isDesktop1024);
    $[37] = granularityApiValue;
    $[38] = isDesktop1024;
    $[39] = isMobile;
    $[40] = isTablet;
    $[41] = selectedElements;
    $[42] = summaryValues;
    $[43] = totalToStartEachBudget;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const series = t13;
  let t14;
  if ($[45] !== allBudgets || $[46] !== budgets || $[47] !== selectAll) {
    const newElements = selectAll.filter(selectAllPath => !allBudgets.some(budget_0 => budget_0.codePath === selectAllPath)).map(_temp);
    const combinedElementsFromAnalytics = [...budgets, ...newElements];
    t14 = sortBy(combinedElementsFromAnalytics, _temp2).map(_temp3);
    $[45] = allBudgets;
    $[46] = budgets;
    $[47] = selectAll;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const categoryOptions = t14;
  let t15;
  if ($[49] !== categoryOptions || $[50] !== selectedElements) {
    let t16;
    if ($[52] !== selectedElements) {
      t16 = item => selectedElements.includes(item.value);
      $[52] = selectedElements;
      $[53] = t16;
    } else {
      t16 = $[53];
    }
    t15 = categoryOptions.filter(t16);
    $[49] = categoryOptions;
    $[50] = selectedElements;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const selectedCategoryOptions = t15;
  const canReset = selectedGranularity !== DEFAULT_GRANULARITY || activeElements !== null;
  let t16;
  if ($[54] !== setActiveElements || $[55] !== setSelectedGranularity) {
    t16 = () => {
      setSelectedGranularity(null);
      setActiveElements(null);
    };
    $[54] = setActiveElements;
    $[55] = setSelectedGranularity;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  const onReset = t16;
  let t17;
  if ($[57] !== setSelectedGranularity) {
    t17 = value => {
      setSelectedGranularity(value);
    };
    $[57] = setSelectedGranularity;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  const onSetSelectedGranularity = t17;
  let t18;
  if ($[59] !== setActiveElements) {
    t18 = values => {
      setActiveElements(values);
    };
    $[59] = setActiveElements;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  const onSetActiveElements = t18;
  let t19;
  if ($[61] !== canReset || $[62] !== categoryOptions || $[63] !== granularityApiValue || $[64] !== isLoading || $[65] !== onReset || $[66] !== onSetActiveElements || $[67] !== onSetSelectedGranularity || $[68] !== selectedCategoryOptions || $[69] !== selectedGranularity || $[70] !== series || $[71] !== titleChart) {
    t19 = {
      titleChart,
      isLoading,
      series,
      selectedGranularity: granularityApiValue,
      selectedGranularityFilter: selectedGranularity,
      setSelectedGranularity: onSetSelectedGranularity,
      granularityOptions,
      categoryOptions,
      selectedCategoryOptions,
      setActiveElements: onSetActiveElements,
      canReset,
      onReset
    };
    $[61] = canReset;
    $[62] = categoryOptions;
    $[63] = granularityApiValue;
    $[64] = isLoading;
    $[65] = onReset;
    $[66] = onSetActiveElements;
    $[67] = onSetSelectedGranularity;
    $[68] = selectedCategoryOptions;
    $[69] = selectedGranularity;
    $[70] = series;
    $[71] = titleChart;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  return t19;
}
function _temp3(budget_1) {
  return {
    label: formatBudgetName(budget_1.name),
    value: budget_1.codePath
  };
}
function _temp2(subBudget) {
  return subBudget.name;
}
function _temp(element_0) {
  return {
    name: element_0,
    codePath: element_0
  };
}
export { useReservesWaterfallChart };